import React from "react";

const TITAN_WTYPE_EMPRESS = 20;
const TITAN_WTYPE_BISON = 21;

const CATEGORY_LABEL = Object.freeze({
  attack: "Attack",
  cast: "Skill",
  move: "Move",
  target: "Target",
  effect: "Effect",
  system: "System",
});

const CATEGORY_CLASS = Object.freeze({
  attack: "attack",
  cast: "skill",
  move: "move",
  target: "target",
  effect: "effect",
  system: "system",
});

const humanizeToken = (raw) => String(raw || "")
  .trim()
  .split(/[_\s]+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  .join(" ");

const normalizeTitanTerminology = (label) => String(label || "")
  .replace(/\bTitan Bison\b/gi, "Source Spirit")
  .replace(/\bBison\b/gi, "Source Spirit")
  .replace(/\bFemale Titan\b/gi, "Empress")
  .replace(/\bMale Titan\b/gi, "Source Spirit");

const shouldShowSkillId = (entry) => {
  const label = String(entry?.label || "").trim();
  return Number.isFinite(Number(entry?.id)) && /^(?:Skill|Spell)\s+\d+$/i.test(label);
};

const formatNumber = (value, digits = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const normalizeCatalogEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    return { label: entry, description: "" };
  }
  return {
    label: String(entry?.label || entry?.name || "").trim(),
    description: String(entry?.description || "").trim(),
    sourceKind: String(entry?.sourceKind || "").trim(),
    officerId: entry?.officerId,
    officerLabel: String(entry?.officerLabel || "").trim(),
    officerIconPath: String(entry?.officerIconPath || "").trim(),
    parentSkillId: entry?.parentSkillId,
  };
};

const resolveSkillEntry = (action, skillCatalogById = {}) => {
  const ids = [action?.skillGid, action?.inferredSkillGid, action?.spellGid, action?.sourceId, action?.sourceGid]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  for (const id of ids) {
    const entry = normalizeCatalogEntry(skillCatalogById?.[String(id)] || skillCatalogById?.[id]);
    if (entry?.label) {
      return {
        id,
        label: normalizeTitanTerminology(entry.label),
        description: entry.description,
        sourceKind: entry.sourceKind,
        officerId: entry.officerId,
        officerLabel: entry.officerLabel,
        officerIconPath: entry.officerIconPath,
        parentSkillId: entry.parentSkillId,
      };
    }
  }
  if (ids.length) {
    const firstId = ids[0];
    return {
      id: firstId,
      label: `${action?.spellGid ? "Spell" : "Skill"} ${firstId}`,
      description: "",
    };
  }
  return null;
};

const resolveTroopEntry = (troopId, troopIdRaw, troopById = new Map(), troopDisplayByGid = {}) => {
  const primary = String(troopId || "").trim();
  const raw = String(troopIdRaw || "").trim();
  if (primary && troopById.has(primary)) {
    const troop = troopById.get(primary);
    return {
      label: String(troop?.label || primary),
      iconPath: String(troop?.iconPath || "").trim(),
      troopId: primary,
    };
  }

  const gidCandidates = [primary, raw].filter(Boolean);
  for (const gid of gidCandidates) {
    const byGid = troopDisplayByGid?.[gid];
    if (!byGid) continue;
    const canonicalTroop = troopById.get(String(byGid?.troopId || ""));
    return {
      label: String(byGid?.label || canonicalTroop?.label || gid),
      iconPath: String(canonicalTroop?.iconPath || byGid?.iconPath || "").trim(),
      troopId: String(byGid?.troopId || gid),
    };
  }

  const fallback = primary || raw;
  if (!fallback) return null;
  return {
    label: fallback,
    iconPath: "",
    troopId: fallback,
  };
};

const resolveSourceVisual = (action, troopById, troopDisplayByGid, skillEntry = null) => {
  if (skillEntry?.sourceKind === "officer") {
    return {
      label: normalizeTitanTerminology(skillEntry.officerLabel || skillEntry.label || "Officer"),
      iconPath: String(skillEntry.officerIconPath || "").trim(),
    };
  }
  const source = action?.source || {};
  const sourceType = String(action?.sourceType || "").trim();
  const sourceTroop = resolveTroopEntry(source?.troopId, source?.troopIdRaw, troopById, troopDisplayByGid);
  if (sourceTroop) {
    return {
      label: normalizeTitanTerminology(sourceTroop.label),
      iconPath: sourceTroop.iconPath,
    };
  }

  const wtype = Number(source?.wtype);
  if (wtype === TITAN_WTYPE_EMPRESS) {
    return {
      label: "Empress",
      iconPath: "/battle-sim-icons/femaleTitan.png",
    };
  }
  if (wtype === TITAN_WTYPE_BISON) {
    return {
      label: "Source Spirit",
      iconPath: "/battle-sim-icons/maleTitan.png",
    };
  }
  if (sourceType.includes("warplane")) {
    return {
      label: "Warplane",
      iconPath: "/battle-sim-icons/warplane.png",
    };
  }
  if (sourceType.includes("titan")) {
    return {
      label: "Titan",
      iconPath: "/battle-sim-icons/femaleTitan.png",
    };
  }
  if (sourceType.includes("skill") || sourceType.includes("spell")) {
    return {
      label: humanizeToken(sourceType),
      iconPath: "",
    };
  }
  return {
    label: "System",
    iconPath: "",
  };
};

const renderTargetLabel = (target, troopById, troopDisplayByGid) => {
  const troop = resolveTroopEntry(target?.troopId, target?.troopIdRaw, troopById, troopDisplayByGid);
  const side = String(target?.side || "").trim();
  const sideLabel = side ? `${side.charAt(0).toUpperCase()}${side.slice(1)}` : "";
  if (troop?.label) {
    return `${sideLabel ? `${sideLabel}: ` : ""}${troop.label}`;
  }
  return sideLabel || "Unknown target";
};

const FrameActionPanel = ({
  visible = true,
  frameActionLog = [],
  troopById = new Map(),
  troopDisplayByGid = {},
  skillCatalogById = {},
}) => {
  if (!visible) return null;

  const actions = Array.isArray(frameActionLog) ? frameActionLog : [];

  return (
    <aside className="battle-frame-actions" aria-label="Frame Actions">
      <div className="battle-frame-actions-header">
        <div className="battle-frame-actions-title">Frame Actions</div>
        <div className="battle-frame-actions-count">{actions.length.toLocaleString()} items</div>
      </div>
      <div className="battle-frame-actions-list">
        {actions.length ? actions.map((action, index) => {
          const skillEntry = resolveSkillEntry(action, skillCatalogById);
          const sourceVisual = resolveSourceVisual(action, troopById, troopDisplayByGid, skillEntry);
          const category = String(action?.category || "system");
          const categoryClass = CATEGORY_CLASS[category] || "system";
          const phaseLabel = String(action?.phase || "Frame");
          const eventType = humanizeToken(action?.eventType || "Event");
          const damage = Number(action?.totalDamage || action?.damage || 0);
          const queuedDamage = Number(action?.totalQueuedDamage || action?.queuedDamage || 0);
          const deadUnits = Number(action?.totalDeadUnits || action?.deadUnits || 0);
          const targetLabels = (Array.isArray(action?.targets) ? action.targets : [])
            .map((target) => renderTargetLabel(target, troopById, troopDisplayByGid))
            .filter(Boolean);
          return (
            <div className="battle-frame-action-item" key={`${action?.index || index}:${action?.eventType || "event"}`}>
              <div className="battle-frame-action-top">
                <span className={`battle-frame-action-badge ${categoryClass}`}>
                  {CATEGORY_LABEL[category] || "Event"}
                </span>
                <span className="battle-frame-action-phase">{phaseLabel}</span>
              </div>
              <div className="battle-frame-action-main">{eventType}</div>
              {skillEntry?.label ? (
                <div
                  className="battle-frame-action-skill"
                  title={skillEntry.description || (skillEntry.id ? `Skill ID ${skillEntry.id}` : "")}
                >
                  {skillEntry.label}
                  {shouldShowSkillId(skillEntry) ? ` (${Number(skillEntry.id)})` : ""}
                </div>
              ) : null}
              <div className="battle-frame-action-source-row">
                {sourceVisual?.iconPath ? (
                  <img src={sourceVisual.iconPath} alt="source" className="battle-frame-action-icon" />
                ) : (
                  <span className="battle-frame-action-icon fallback">{String(sourceVisual?.label || "S").slice(0, 1)}</span>
                )}
                <span className="battle-frame-action-source">{sourceVisual?.label || "Unknown"}</span>
                {action?.source?.side ? (
                  <span className="battle-frame-action-side">{String(action.source.side).toUpperCase()}</span>
                ) : null}
              </div>
              {targetLabels.length ? (
                <div className="battle-frame-action-targets">
                  Targets: {targetLabels.slice(0, 4).join(", ")}
                  {targetLabels.length > 4 ? ` +${targetLabels.length - 4} more` : ""}
                </div>
              ) : null}
              {(damage > 0 || queuedDamage > 0 || deadUnits > 0) ? (
                <div className="battle-frame-action-metrics">
                  {damage > 0 ? `Damage ${formatNumber(damage)}` : ""}
                  {queuedDamage > 0 ? `${damage > 0 ? " | " : ""}Queued ${formatNumber(queuedDamage)}` : ""}
                  {deadUnits > 0 ? `${(damage > 0 || queuedDamage > 0) ? " | " : ""}Kills ${formatNumber(deadUnits, 2)}` : ""}
                </div>
              ) : null}
            </div>
          );
        }) : (
          <div className="battle-frame-actions-empty">No action events on this frame.</div>
        )}
      </div>
    </aside>
  );
};

export default FrameActionPanel;
